import React from "react";
import { ErrorNotificationProps, Models } from "@hex-insights/app-modules";
import { ChildrenProps, NotificationContainer, NotificationRenderProps, PartialKeys, Row } from "@hex-insights/core";
import { PermissionRequired } from "@hex-insights/permissioning";
import {
	permissions,
	SendParentPortalInviteMessageMutation,
	SendUserInviteMessageMutation,
	User,
	useSendParentPortalInviteMessageMutation,
	useSendUserInviteMessageMutation,
} from "../../Utilities";
import { Icon } from "../Icon";

export type InviteButtonPermissionRequiredProps = Partial<ChildrenProps>;

export function InviteButtonPermissionRequired({ children }: InviteButtonPermissionRequiredProps) {
	return <PermissionRequired requiredPermission={permissions.User.Create.Admin}>{children}</PermissionRequired>;
}

const defaultConfirmButtonProps: Models.ActionButtonWithConfirmationProps["confirmButtonProps"] = {
	children: "Send",
};

export type PortalInviteButtonProps = PartialKeys<
	Omit<Models.ActionButtonWithConfirmationProps<SendUserInviteMessageMutation>, "performAction">,
	"confirmationContent"
> & {
	userID: User["id"];
};

export function PortalInviteButton({
	userID,
	variant = "primary",
	confirmationContent = "Please confirm.",
	confirmButtonProps = defaultConfirmButtonProps,
	ErrorNotification = DefaultErrorNotification,
	SuccessNotification = DefaultSuccessNotification,
	children = (
		<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
			<Icon.PaperPlaneTilt size={16} /> Send Portal Invite
		</Row>
	),
	...props
}: PortalInviteButtonProps) {
	const [sendInvite] = useSendUserInviteMessageMutation();
	const performAction = React.useCallback(async () => {
		return sendInvite({ variables: { userID } });
	}, [sendInvite, userID]);

	return (
		<Models.ActionButtonWithConfirmation
			performAction={performAction}
			variant={variant}
			confirmationContent={confirmationContent}
			confirmButtonProps={confirmButtonProps}
			ErrorNotification={ErrorNotification}
			SuccessNotification={SuccessNotification}
			children={children}
			{...props}
		/>
	);
}

export type ParentPortalInviteButtonProps = PartialKeys<
	Omit<Models.ActionButtonWithConfirmationProps<SendParentPortalInviteMessageMutation>, "performAction">,
	"confirmationContent"
> & {
	userID: User["id"];
};

export function ParentPortalInviteButton({
	userID,
	variant = "primary",
	confirmationContent = "Please confirm.",
	confirmButtonProps = defaultConfirmButtonProps,
	ErrorNotification = DefaultErrorNotification,
	SuccessNotification = DefaultSuccessNotification,
	children = (
		<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
			<Icon.PaperPlaneTilt size={16} /> Send Portal Invite
		</Row>
	),
	...props
}: ParentPortalInviteButtonProps) {
	const [sendInvite] = useSendParentPortalInviteMessageMutation();
	const performAction = React.useCallback(async () => {
		return sendInvite({ variables: { userID } });
	}, [sendInvite, userID]);

	return (
		<Models.ActionButtonWithConfirmation
			performAction={performAction}
			variant={variant}
			confirmationContent={confirmationContent}
			confirmButtonProps={confirmButtonProps}
			ErrorNotification={ErrorNotification}
			SuccessNotification={SuccessNotification}
			children={children}
			{...props}
		/>
	);
}

function DefaultErrorNotification(props: ErrorNotificationProps) {
	return (
		<NotificationContainer variant="danger" {...props}>
			Something went wrong, please try again later.
		</NotificationContainer>
	);
}

function DefaultSuccessNotification(props: NotificationRenderProps) {
	return (
		<NotificationContainer variant="success" {...props}>
			Invite sent!
		</NotificationContainer>
	);
}
